/**
 * Display-only "social proof" Bestellzahl pro Produkt.
 * - Top-Seller liegen rund um 3'000.
 * - Schwächere Produkte deutlich darunter.
 * - Jedes Produkt hat eine eigene, eindeutige Zahl (kein Doppel).
 * - Deterministisch (gleicher Slug → gleiche Zahl).
 * Wird identisch auf der ProductPage UND im Warenkorb verwendet.
 * KEIN Einfluss auf die "Zuletzt bestellt"-Logik.
 */

// Direkt kalibrierte Werte pro Slug. Top-Seller ~3000, abnehmend.
// Alle Werte einzigartig — keine Duplikate.
const SLUG_BADGE_COUNTS: Record<string, number> = {
  // Top-Seller (~2'700–3'100)
  'instagram-follower-kaufen': 3120,
  'tiktok-likes': 2980,
  'instagram-likes': 2740,
  'tiktok-views': 2610,

  // Stark (~1'400–2'200)
  'tiktok-follower-kaufen': 2180,
  'instagram-views': 1960,
  'deutsche-instagram-follower-kaufen': 1740,
  'deutsche-instagram-likes-kaufen': 1520,

  // Mittel (~700–1'200)
  'instagram-wachstum-paket': 1180,
  'instagram-kommentare': 940,
  'linkedin-follower': 820,
  'youtube-likes': 760,

  // Niedriger (~350–650)
  'italienische-instagram-likes-kaufen': 640,
  'youtube-views': 590,
  'tiktok-saves': 540,
  'facebook-follower': 500,
  'spotify-monatliche-hoerer': 470,
  'franzoesische-instagram-follower-kaufen': 430,
  'instagram-story-views': 410,
  'linkedin-likes': 380,
  'youtube-abonnenten': 360,

  // Nische (~180–340)
  'facebook-page-follower': 340,
  'facebook-page-likes': 320,
  'franzoesische-instagram-likes-kaufen': 300,
  'italienische-instagram-follower-kaufen': 280,
  'twitch-follower': 270,
  'usa-instagram-follower-kaufen': 250,
  'spotify-streams': 240,
  'telegram-kanal-mitglieder': 230,
  'threads-likes': 220,
  'tiktok-kommentare': 210,
  'twitter-likes': 200,
  'twitter-views': 195,
  'verifizierte-instagram-follower-mit-blauem-haken': 185,

  // Sehr klein (~60–170) — alle eindeutig
  'facebook-gruppenmitglieder': 170,
  'linkedin-views': 160,
  'reddit-kanal-subscriber': 155,
  'reddit-follower': 145,
  'soundcloud-follower': 140,
  'soundcloud-likes': 130,
  'soundcloud-plays': 125,
  'soundcloud-reposts': 115,
  'spotify-follower': 110,
  'telegram-gruppenmitglieder': 100,
  'telegram-post-views': 95,
  'telegram-reaktionen': 85,
  'threads-follower': 80,
  'twitter-follower': 75,
  'youtube-shorts-views': 65,
};

/**
 * Stabile Bestellzahl für die Social-Proof-Badge.
 * Bei unbekanntem Slug: Plattform-basierter Fallback mit deterministischer Variance,
 * sodass auch dort kein Wert doppelt vorkommt.
 */
export function getOrderCount(productId: string, productSlug: string): number {
  const slug = (productSlug || '').toLowerCase();

  const direct = SLUG_BADGE_COUNTS[slug];
  if (direct !== undefined) return direct;

  // Deterministischer Hash für Fallback-Variance
  let hash = 0;
  const seed = slug || productId || '';
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  const h = Math.abs(hash);

  let base: number;
  if (slug.includes('instagram') && slug.includes('follower')) base = 1500;
  else if (slug.includes('instagram')) base = 900;
  else if (slug.includes('tiktok') && slug.includes('follower')) base = 1300;
  else if (slug.includes('tiktok')) base = 800;
  else if (slug.includes('youtube')) base = 500;
  else if (slug.includes('spotify')) base = 350;
  else if (slug.includes('facebook')) base = 300;
  else if (slug.includes('twitter') || slug.includes('x-')) base = 200;
  else if (slug.includes('telegram')) base = 180;
  else if (slug.includes('twitch')) base = 220;
  else if (slug.includes('linkedin')) base = 250;
  else base = 120;

  // ±20 % deterministische Variance + Offset, damit Werte einzigartig wirken
  const variancePct = (h % 41) - 20; // -20..+20
  const offset = (h % 17) - 8;       // -8..+8 (zerstört Round-Kollisionen)
  const value = Math.round(base * (1 + variancePct / 100)) + offset;

  return Math.max(50, Math.round(value / 5) * 5);
}
